/**
 * useExportPDF.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook d'export PDF pour le Top N Dashboard – Nawara BI
 *
 * Structure du PDF généré :
 *  • Page de couverture
 *  • Pour chaque panel : 1 page par type d'affichage
 *    (Tableau, Barres, Lignes, Aire, Camembert, Radar)
 *
 * Dépendances :
 *   npm install jspdf html2canvas
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from "react";
import jsPDF from "jspdf";


// ── Types ─────────────────────────────────────────────────────────────────────
export interface RankRowPDF {
  member: string;
  ca: number;
  qte?: number;
}

export interface PanelDataPDF {
  id: string;
  title: string;
  icon: string;
  color: string; // hex avec # ex: "#1D5BA8"
  rows: RankRowPDF[] | null;
}

export interface ExportPDFOptions {
  filename: string;
  dateFrom?: string;
  dateTo?: string;
  topN: number;
  panels: PanelDataPDF[];
}

// ── Filtre valeurs indésirables ──────────────────────────────────────────────
const INVALID_DIM_RE = /^\s*(all|unknown|couleur inconnue|inconnue|non renseign[ée]|n\/a|na|null|undefined|#n\/a)\s*$/i;
/** Retourne false si la valeur est vide, "all", "Unknown", "inconnue", etc. */
function isValidDimension(v: string | null | undefined): boolean {
  if (!v || v.trim() === "") return false;
  return !INVALID_DIM_RE.test(v.trim());
}

// ── Palette & utilitaires ─────────────────────────────────────────────────────

const CHART_TYPE_LABELS: Record<string, string> = {
  table: "Tableau",
  bar: "Barres",
  line: "Lignes",
  area: "Aire",
  pie: "Camembert",
  radar: "Radar",
};

const CHART_TYPES = ["table", "bar", "line", "area", "pie", "radar"] as const;
type ChartTypePDF = (typeof CHART_TYPES)[number];

const PIE_COLORS = [
  "#7C3AED",
  "#DB2777",
  "#059669",
  "#2563EB",
  "#D97706",
  "#DC2626",
  "#0891B2",
  "#EA580C",
  "#9333EA",
  "#BE185D",
];

const MEDAL_COLORS: Record<number, string> = {
  1: "#D98E04",
  2: "#64748B",
  3: "#8A5A02",
};

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function lightenRgb(hex: string, amount: number): [number, number, number] {
  const [r, g, b] = hexToRgb(hex);
  return [
    Math.round(r + (255 - r) * amount),
    Math.round(g + (255 - g) * amount),
    Math.round(b + (255 - b) * amount),
  ];
}

function fmt(n: number): string {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n);
}

// ── Dimensions PDF ────────────────────────────────────────────────────────────
const PW = 297; // A4 landscape width mm
const PH = 210; // A4 landscape height mm
const ML = 14; // margin left
const MT = 14; // margin top
const MR = 14; // margin right
const CW = PW - ML - MR; // content width

// ── Helpers dessin jsPDF ──────────────────────────────────────────────────────

function setFill(doc: jsPDF, hex: string, alpha = 1) {
  const [r, g, b] = hexToRgb(hex);
  doc.setFillColor(r, g, b);
}

function setDraw(doc: jsPDF, hex: string) {
  const [r, g, b] = hexToRgb(hex);
  doc.setDrawColor(r, g, b);
}

function setTextColor(doc: jsPDF, hex: string) {
  const [r, g, b] = hexToRgb(hex);
  doc.setTextColor(r, g, b);
}

function setTextColorRGB(doc: jsPDF, r: number, g: number, b: number) {
  doc.setTextColor(r, g, b);
}

function roundedRect(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  style: "F" | "D" | "FD" = "F",
) {
  doc.roundedRect(x, y, w, h, r, r, style);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Page de couverture ────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawCoverPage(
  doc: jsPDF,
  panels: PanelDataPDF[],
  topN: number,
  dateFrom?: string,
  dateTo?: string,
) {
  // Fond dégradé simulé
  setFill(doc, "#EEF4FB");
  doc.rect(0, 0, PW, PH, "F");

  // Bande supérieure
  setFill(doc, "#1D5BA8");
  doc.rect(0, 0, PW, 48, "F");

  // Titre principal
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  setTextColor(doc, "#FFFFFF");
  doc.text("Top N Dashboard – AvDash", ML, 28);

  // Sous-titre
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  setTextColor(doc, "#D7E5F5");
  const periodeStr =
    dateFrom && dateTo
      ? `Période : ${dateFrom} → ${dateTo}  |  Top ${topN}`
      : `Toutes dates  |  Top ${topN}`;
  doc.text(periodeStr, ML, 40);

  // Date de génération
  doc.setFontSize(9);
  setTextColor(doc, "#B0CBEB");
  doc.text(`Généré le : ${new Date().toLocaleString("fr-FR")}`, PW - MR, 40, {
    align: "right",
  });

  // Sommaire des panels
  let y = 62;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setTextColor(doc, "#1D5BA8");
  doc.text("Sommaire", ML, y);
  y += 6;

  setDraw(doc, "#1D5BA8");
  doc.setLineWidth(0.5);
  doc.line(ML, y, ML + 40, y);
  y += 6;

  const cols = 3;
  const colW = CW / cols;
  panels.forEach((panel, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = ML + col * colW;
    const py = y + row * 22;

    // Carte mini
    const [lr, lg, lb] = lightenRgb(panel.color, 0.88);
    doc.setFillColor(lr, lg, lb);
    roundedRect(doc, x, py, colW - 4, 18, 3, "F");

    // Barre couleur gauche
    setFill(doc, panel.color);
    doc.rect(x, py, 3, 18, "F");

    // Texte
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setTextColor(doc, panel.color);
    doc.text(panel.title, x + 6, py + 7);

    const rowCount = panel.rows ? panel.rows.length : 0;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setTextColorRGB(doc, 100, 80, 140);
    doc.text(
      rowCount > 0
        ? `Top ${topN} · CA: ${fmt(panel.rows!.reduce((s, r) => s + r.ca, 0))}`
        : "Aucune donnée",
      x + 6,
      py + 13,
    );
  });

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setTextColorRGB(doc, 160, 140, 200);
  doc.text("AvDash – Rapport d'analyse Top N", ML, PH - 6);
  doc.text("Page 1", PW - MR, PH - 6, { align: "right" });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Header de page panel ──────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawPageHeader(
  doc: jsPDF,
  panel: PanelDataPDF,
  chartType: ChartTypePDF,
  topN: number,
  pageNum: number,
  totalPages: number,
) {
  // Bande header
  setFill(doc, panel.color);
  doc.rect(0, 0, PW, 18, "F");

  // Titre
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setTextColor(doc, "#FFFFFF");
  doc.text(`${panel.title}  —  ${CHART_TYPE_LABELS[chartType]}`, ML, 12);

  // Top N
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setTextColor(doc, "#D7E5F5");
  doc.text(`Top ${topN}`, PW - MR, 12, { align: "right" });

  // Sous bande légère
  const [lr, lg, lb] = lightenRgb(panel.color, 0.9);
  doc.setFillColor(lr, lg, lb);
  doc.rect(0, 18, PW, 7, "F");

  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  setTextColor(doc, panel.color);
  doc.text(
    `AvDash · ${CHART_TYPE_LABELS[chartType]}  |  Page ${pageNum} / ${totalPages}`,
    ML,
    23,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── En-tête de colonnes tableau (réutilisable sur chaque page) ────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawTableColumnHeaders(
  doc: jsPDF,
  panel: PanelDataPDF,
  startY: number,
  colXs: number[],
  colWidths: number[],
  rowH: number,
) {
  const headers = [
    "#",
    "Membre / Libellé",
    "CA (DT)",
    "Quantité",
    "% du Total",
  ];

  setFill(doc, panel.color);
  doc.rect(ML, startY, CW, rowH + 1, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColor(doc, "#FFFFFF");
  headers.forEach((h, i) => {
    // # → center | Membre → left | CA → right (avec padding) | Qté → left | % → right
    const align =
      i === 0 ? "center" : i === 1 ? "left" : i === 3 ? "left" : "right";
    // CA column (i===2) gets extra 5mm right padding so numbers never touch Qté
    const rightPad = i === 2 ? 5 : 2;
    const x =
      align === "right"
        ? colXs[i] + colWidths[i] - rightPad
        : align === "center"
          ? colXs[i] + colWidths[i] / 2
          : colXs[i] + 2;
    doc.text(h, x, startY + 7, { align });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Tableau multi-pages ───────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AVANT : drawTable dessinait tout sur une seule page → les lignes débordaient
 *         hors de la page sans jamais créer de nouvelle page.
 *
 * APRÈS : drawTable détecte quand il reste moins d'une ligne de hauteur avant
 *         le footer, ajoute une nouvelle page avec son propre header/colonnes,
 *         et continue à dessiner. Retourne le nouveau pageNum courant.
 */
function drawTable(
  doc: jsPDF,
  panel: PanelDataPDF,
  topN: number,
  // Paramètres de pagination passés depuis le hook principal
  currentPageNum: number,
  totalPages: number,
  chartType: ChartTypePDF,
): number {
  if (!panel.rows || panel.rows.length === 0) return currentPageNum;

  const rows = panel.rows;
  const total = rows.reduce((s, r) => s + r.ca, 0);

  const rowH = 9;
  // Colonnes : # | Membre | CA (DT) | Quantité | % du Total
  // Total = 12+58+105+58+36 = 269 = CW (297-14-14)
  // CA très large pour que les grands nombres ne chevauchent jamais Quantité
  const colWidths = [12, 58, 105, 58, 36];
  const colXs: number[] = [];
  let cx = ML;
  colWidths.forEach((w) => {
    colXs.push(cx);
    cx += w;
  });

  // ── Zone utilisable ───────────────────────────────────────────────────────
  // Header panel = 25mm, footer = 10mm → zone data : 25 → PH-10
  const HEADER_H = 25; // espace réservé en haut (header panel déjà dessiné)
  const FOOTER_H = 10; // espace réservé en bas pour le footer
  const PAGE_DATA_BOTTOM = PH - FOOTER_H;

  // Première page : la zone de données commence après le header du panel (25mm)
  // On laisse 2mm de marge après le header
  let startY = HEADER_H + 2;

  // Dessiner l'en-tête des colonnes
  drawTableColumnHeaders(doc, panel, startY, colXs, colWidths, rowH);
  let y = startY + rowH + 1; // première ligne de données

  let pageNum = currentPageNum;

  // ── Dessin ligne par ligne avec pagination ────────────────────────────────
  rows.forEach((row, idx) => {
    const rank = idx + 1;
    const pct = total > 0 ? (row.ca / total) * 100 : 0;

    // ── Vérifier si on dépasse la zone disponible (laisser rowH + FOOTER_H) ──
    if (y + rowH > PAGE_DATA_BOTTOM) {
      // Dessiner le footer de la page courante
      drawFooter(doc, panel, pageNum, totalPages);

      // Nouvelle page
      doc.addPage();
      pageNum++;

      // Header panel sur la nouvelle page
      drawPageHeader(doc, panel, chartType, topN, pageNum, totalPages);

      // Réinitialiser y après le header
      startY = HEADER_H + 2;
      drawTableColumnHeaders(doc, panel, startY, colXs, colWidths, rowH);
      y = startY + rowH + 1;
    }

    // Fond alterné
    if (idx % 2 === 1) {
      const [lr, lg, lb] = lightenRgb(panel.color, 0.93);
      doc.setFillColor(lr, lg, lb);
      doc.rect(ML, y, CW, rowH, "F");
    }

    // Fond médaille top 3
    if (rank <= 3) {
      const mc = MEDAL_COLORS[rank] ?? panel.color;
      const [lr, lg, lb] = lightenRgb(mc, 0.88);
      doc.setFillColor(lr, lg, lb);
      doc.rect(ML, y, CW, rowH, "F");
    }

    // Rang
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    if (rank <= 3) {
      const mc = MEDAL_COLORS[rank] ?? panel.color;
      setTextColor(doc, mc);
    } else {
      setTextColorRGB(doc, 80, 60, 120);
    }
    const medal =
      rank === 1 ? "1" : rank === 2 ? "2" : rank === 3 ? "3" : String(rank);
    doc.text(medal, colXs[0] + colWidths[0] / 2, y + 6.5, { align: "center" });

    // Membre
    doc.setFont("helvetica", rank === 1 ? "bold" : "normal");
    doc.setFontSize(8.5);
    setTextColorRGB(doc, 30, 16, 64);
    const memberStr =
      row.member.length > 22 ? row.member.slice(0, 20) + "…" : row.member;
    doc.text(memberStr, colXs[1] + 2, y + 6.5);

    // CA
    doc.setFont("helvetica", rank === 1 ? "bold" : "normal");
    doc.setFontSize(8.5);
    setTextColor(doc, rank === 1 ? panel.color : "#0D2A50");
    doc.text(fmt(row.ca), colXs[2] + colWidths[2] - 10, y + 6.5, {
      align: "right",
    });

    // Qte — chiffre aligné à GAUCHE de la colonne Qte (pas à droite)
    // pour laisser de la place à la barre qui est au milieu/droite
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    setTextColorRGB(doc, 100, 80, 140);
    const qteStr = row.qte !== undefined ? fmt(row.qte) : "–";
    doc.text(qteStr, colXs[3] + 2, y + 6.5, { align: "left" });

    // Barre de progression — dans la colonne % (colXs[4]), toute la largeur
    const barW = colWidths[4] - 14; // laisse 13mm à droite pour le texte %
    const barH = 3;
    const barY = y + 3.5;
    const barX = colXs[4] + 1;
    const [lr, lg, lb] = lightenRgb(panel.color, 0.8);
    doc.setFillColor(lr, lg, lb);
    doc.rect(barX, barY, barW, barH, "F");
    setFill(doc, panel.color);
    doc.rect(barX, barY, barW * Math.min(pct / 100, 1), barH, "F");

    // % — texte à droite de la colonne %
    doc.setFontSize(8);
    setTextColor(doc, panel.color);
    doc.text(pct.toFixed(1) + "%", colXs[4] + colWidths[4] - 1, y + 6.5, {
      align: "right",
    });

    // Ligne séparatrice
    setDraw(doc, panel.color);
    doc.setLineWidth(0.1);
    doc.line(ML, y + rowH, ML + CW, y + rowH);

    y += rowH;
  });

  // ── Ligne Total ───────────────────────────────────────────────────────────
  // Vérifier si la ligne total tient sur la page courante
  if (y + rowH + 2 > PAGE_DATA_BOTTOM) {
    drawFooter(doc, panel, pageNum, totalPages);
    doc.addPage();
    pageNum++;
    drawPageHeader(doc, panel, chartType, topN, pageNum, totalPages);
    y = HEADER_H + 4;
  }

  const totalY = y + 2;
  const [lr, lg, lb] = lightenRgb(panel.color, 0.82);
  doc.setFillColor(lr, lg, lb);
  doc.rect(ML, totalY, CW, rowH + 1, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColor(doc, panel.color);
  doc.text("TOTAL", colXs[1] + 2, totalY + 7);
  doc.text(fmt(total), colXs[2] + colWidths[2] - 2, totalY + 7, {
    align: "right",
  });

  return pageNum;
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Graphique en barres ───────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawBarChart(doc: jsPDF, panel: PanelDataPDF, topN: number) {
  if (!panel.rows || panel.rows.length === 0) return;

  const rows = panel.rows.slice(0, Math.min(topN, 20));
  const maxCa = rows[0]?.ca ?? 1;

  const chartX = ML;
  const chartY = 34;
  const chartW = CW;
  const chartH = 148;
  const barCount = rows.length;
  const barW = Math.min(28, (chartW - 20) / barCount - 3);
  const gap = (chartW - 20 - barW * barCount) / (barCount + 1);

  // Fond
  const [lr, lg, lb] = lightenRgb(panel.color, 0.96);
  doc.setFillColor(lr, lg, lb);
  roundedRect(doc, chartX, chartY, chartW, chartH, 4, "F");

  // Grille horizontale
  const gridLines = 5;
  setDraw(doc, panel.color);
  doc.setLineWidth(0.15);
  for (let i = 0; i <= gridLines; i++) {
    const gy = chartY + chartH - 20 - ((chartH - 30) * i) / gridLines;
    const gv = (maxCa * i) / gridLines;
    doc.setLineDashPattern([1, 2], 0);
    const [dlr, dlg, dlb] = lightenRgb(panel.color, 0.7);
    doc.setDrawColor(dlr, dlg, dlb);
    doc.line(chartX + 18, gy, chartX + chartW - 4, gy);

    // Label axe Y
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    setTextColor(doc, panel.color);
    const label =
      gv >= 1_000_000
        ? `${(gv / 1_000_000).toFixed(1)}M`
        : gv >= 1000
          ? `${(gv / 1000).toFixed(0)}k`
          : fmt(gv);
    doc.text(label, chartX + 16, gy + 1, { align: "right" });
  }
  doc.setLineDashPattern([], 0);

  // Barres
  rows.forEach((row, idx) => {
    const x = chartX + 20 + gap + idx * (barW + gap);
    const barH2 = (row.ca / maxCa) * (chartH - 30);
    const y = chartY + chartH - 20 - barH2;

    // Couleur médaille ou couleur panel
    const barColor =
      idx < 3 ? (MEDAL_COLORS[idx + 1] ?? panel.color) : panel.color;

    setFill(doc, barColor);
    roundedRect(doc, x, y, barW, barH2, 2, "F");

    // Valeur au dessus
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    setTextColor(doc, barColor);
    const vLabel =
      row.ca >= 1_000_000
        ? `${(row.ca / 1_000_000).toFixed(1)}M`
        : row.ca >= 1000
          ? `${(row.ca / 1000).toFixed(0)}k`
          : fmt(row.ca);
    doc.text(vLabel, x + barW / 2, y - 1.5, { align: "center" });

    // Label axe X
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    setTextColorRGB(doc, 80, 60, 120);
    const lb2 =
      row.member.length > 8 ? row.member.slice(0, 7) + "…" : row.member;
    doc.text(lb2, x + barW / 2, chartY + chartH - 8, {
      align: "center",
      maxWidth: barW + gap,
    });
  });

  // Axe X baseline
  setDraw(doc, panel.color);
  doc.setLineWidth(0.4);
  doc.line(
    chartX + 18,
    chartY + chartH - 20,
    chartX + chartW - 4,
    chartY + chartH - 20,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Graphique en lignes ───────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawLineChart(doc: jsPDF, panel: PanelDataPDF, topN: number) {
  if (!panel.rows || panel.rows.length === 0) return;

  const rows = panel.rows.slice(0, Math.min(topN, 20));
  const maxCa = rows[0]?.ca ?? 1;

  const chartX = ML;
  const chartY = 34;
  const chartW = CW;
  const chartH = 148;

  // Fond
  const [lr, lg, lb] = lightenRgb(panel.color, 0.96);
  doc.setFillColor(lr, lg, lb);
  roundedRect(doc, chartX, chartY, chartW, chartH, 4, "F");

  const plotX = chartX + 22;
  const plotY = chartY + 8;
  const plotW = chartW - 28;
  const plotH = chartH - 26;

  // Grille
  const gridLines = 5;
  for (let i = 0; i <= gridLines; i++) {
    const gy = plotY + plotH - (plotH * i) / gridLines;
    const gv = (maxCa * i) / gridLines;
    const [dlr, dlg, dlb] = lightenRgb(panel.color, 0.7);
    doc.setDrawColor(dlr, dlg, dlb);
    doc.setLineWidth(0.15);
    doc.setLineDashPattern([1, 2], 0);
    doc.line(plotX, gy, plotX + plotW, gy);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    setTextColor(doc, panel.color);
    const label =
      gv >= 1_000_000
        ? `${(gv / 1_000_000).toFixed(1)}M`
        : gv >= 1000
          ? `${(gv / 1000).toFixed(0)}k`
          : fmt(gv);
    doc.text(label, plotX - 2, gy + 1, { align: "right" });
  }
  doc.setLineDashPattern([], 0);

  // Points et ligne
  const step = plotW / (rows.length - 1 || 1);
  const points = rows.map((row, idx) => ({
    x: plotX + idx * step,
    y: plotY + plotH - (row.ca / maxCa) * plotH,
    row,
  }));

  // Aire sous la courbe
  if (points.length > 1) {
    const [pr, pg, pb] = hexToRgb(panel.color);
    doc.setFillColor(pr, pg, pb);
    // Dessiner polygone aire
    const areaPoints: number[] = [];
    areaPoints.push(points[0].x, plotY + plotH);
    points.forEach((p) => areaPoints.push(p.x, p.y));
    areaPoints.push(points[points.length - 1].x, plotY + plotH);
    // jsPDF triangle approximation via lines
    doc.setFillColor(pr, pg, pb);
    doc.setGState(doc.GState({ opacity: 0.12 }));
    // Draw filled poly
    const firstPt = { x: points[0].x, y: plotY + plotH };
    doc.moveTo(firstPt.x, firstPt.y);
    points.forEach((p) => doc.lineTo(p.x, p.y));
    doc.lineTo(points[points.length - 1].x, plotY + plotH);
    doc.lineTo(firstPt.x, firstPt.y);
    doc.fill();
    doc.setGState(doc.GState({ opacity: 1 }));
  }

  // Ligne principale
  setDraw(doc, panel.color);
  doc.setLineWidth(1.5);
  for (let i = 1; i < points.length; i++) {
    doc.line(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
  }

  // Points
  points.forEach((p, idx) => {
    const pc = idx < 3 ? (MEDAL_COLORS[idx + 1] ?? panel.color) : panel.color;
    setFill(doc, pc);
    doc.circle(p.x, p.y, 1.8, "F");

    // Label X
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    setTextColorRGB(doc, 80, 60, 120);
    const lb2 =
      p.row.member.length > 8 ? p.row.member.slice(0, 7) + "…" : p.row.member;
    doc.text(lb2, p.x, plotY + plotH + 5, { align: "center" });
  });

  // Axe X
  setDraw(doc, panel.color);
  doc.setLineWidth(0.4);
  doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Graphique Aire ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawAreaChart(doc: jsPDF, panel: PanelDataPDF, topN: number) {
  // Aire = ligne + remplissage — on réutilise drawLineChart qui dessine déjà l'aire
  // On marque juste le titre différent dans le header
  drawLineChart(doc, panel, topN);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Camembert ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawPieChart(doc: jsPDF, panel: PanelDataPDF, topN: number) {
  if (!panel.rows || panel.rows.length === 0) return;

  const rows = panel.rows.slice(0, Math.min(topN, 10));
  const total = rows.reduce((s, r) => s + r.ca, 0);

  const cx = ML + CW * 0.38;
  const cy = 34 + 80;
  const radius = 68;

  // Fond
  const [lr, lg, lb] = lightenRgb(panel.color, 0.96);
  doc.setFillColor(lr, lg, lb);
  roundedRect(doc, ML, 34, CW, 152, 4, "F");

  // Camembert (secteurs)
  let startAngle = -Math.PI / 2;
  rows.forEach((row, idx) => {
    const pct = total > 0 ? row.ca / total : 0;
    const endAngle = startAngle + pct * 2 * Math.PI;
    const color = PIE_COLORS[idx % PIE_COLORS.length];

    setFill(doc, color);
    // jsPDF pie sector via ellipse segments
    const steps = Math.max(4, Math.round(pct * 36));
    const pts: [number, number][] = [[cx, cy]];
    for (let s = 0; s <= steps; s++) {
      const angle = startAngle + (s / steps) * (endAngle - startAngle);
      pts.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
    }
    pts.push([cx, cy]);

    // Draw filled polygon
    if (pts.length >= 3) {
      doc.setGState(doc.GState({ opacity: 1 }));
      const [pr, pg, pb] = hexToRgb(color);
      doc.setFillColor(pr, pg, pb);
      doc.triangle(
        pts[0][0],
        pts[0][1],
        pts[1][0],
        pts[1][1],
        pts[2 % pts.length][0],
        pts[2 % pts.length][1],
        "F",
      );
      // Draw all points as a path
      const path =
        pts
          .map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`))
          .join(" ") + " Z";
      // Use lines as fallback
      doc.setLineWidth(0);
      for (let s = 1; s < pts.length - 1; s++) {
        doc.triangle(
          cx,
          cy,
          pts[s][0],
          pts[s][1],
          pts[s + 1]?.[0] ?? cx,
          pts[s + 1]?.[1] ?? cy,
          "F",
        );
      }
    }

    // Étiquette sur le secteur
    const midAngle = (startAngle + endAngle) / 2;
    const labelR = radius * 0.65;
    const lx = cx + Math.cos(midAngle) * labelR;
    const ly = cy + Math.sin(midAngle) * labelR;
    if (pct > 0.04) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      setTextColor(doc, "#FFFFFF");
      doc.text(`${Math.round(pct * 100)}%`, lx, ly, { align: "center" });
    }

    startAngle = endAngle;
  });

  // Bordure cercle
  setDraw(doc, panel.color);
  doc.setLineWidth(0.3);
  doc.circle(cx, cy, radius, "D");

  // Légende droite
  const legX = ML + CW * 0.68;
  let legY = 46;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColor(doc, panel.color);
  doc.text("Légende", legX, legY);
  legY += 6;

  rows.forEach((row, idx) => {
    const pct = total > 0 ? (row.ca / total) * 100 : 0;
    const color = PIE_COLORS[idx % PIE_COLORS.length];

    setFill(doc, color);
    roundedRect(doc, legX, legY, 7, 5, 1, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setTextColorRGB(doc, 30, 16, 64);
    const label =
      row.member.length > 22 ? row.member.slice(0, 20) + "…" : row.member;
    doc.text(label, legX + 10, legY + 4);

    doc.setFont("helvetica", "bold");
    setTextColor(doc, color);
    doc.text(`${pct.toFixed(1)}%  ·  ${fmt(row.ca)} DT`, legX + 10, legY + 9);

    legY += 14;
    if (legY > 34 + 148) return;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Radar ────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawRadarChart(doc: jsPDF, panel: PanelDataPDF, topN: number) {
  if (!panel.rows || panel.rows.length === 0) return;

  const rows = panel.rows.slice(0, Math.min(topN, 10));
  const maxCa = rows[0]?.ca ?? 1;
  const n = rows.length;

  const cx = ML + CW / 2;
  const cy = 34 + 80;
  const R = 72;

  // Fond
  const [lr, lg, lb] = lightenRgb(panel.color, 0.96);
  doc.setFillColor(lr, lg, lb);
  roundedRect(doc, ML, 34, CW, 152, 4, "F");

  // Grilles concentriques
  const levels = 4;
  for (let l = 1; l <= levels; l++) {
    const r = (R * l) / levels;
    const [dlr, dlg, dlb] = lightenRgb(panel.color, 0.6);
    doc.setDrawColor(dlr, dlg, dlb);
    doc.setLineWidth(0.2);
    doc.circle(cx, cy, r, "D");
  }

  // Axes
  const angle0 = -Math.PI / 2;
  rows.forEach((_, idx) => {
    const angle = angle0 + (idx / n) * 2 * Math.PI;
    const [dlr, dlg, dlb] = lightenRgb(panel.color, 0.5);
    doc.setDrawColor(dlr, dlg, dlb);
    doc.setLineWidth(0.2);
    doc.line(cx, cy, cx + Math.cos(angle) * R, cy + Math.sin(angle) * R);
  });

  // Polygone données
  const [pr, pg, pb] = hexToRgb(panel.color);
  doc.setFillColor(pr, pg, pb);
  doc.setGState(doc.GState({ opacity: 0.22 }));

  const pts = rows.map((row, idx) => {
    const angle = angle0 + (idx / n) * 2 * Math.PI;
    const r = (row.ca / maxCa) * R;
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r] as [
      number,
      number,
    ];
  });

  if (pts.length >= 3) {
    for (let i = 1; i < pts.length - 1; i++) {
      doc.triangle(
        cx,
        cy,
        pts[i][0],
        pts[i][1],
        pts[i + 1][0],
        pts[i + 1][1],
        "F",
      );
    }
    doc.triangle(
      cx,
      cy,
      pts[pts.length - 1][0],
      pts[pts.length - 1][1],
      pts[0][0],
      pts[0][1],
      "F",
    );
  }
  doc.setGState(doc.GState({ opacity: 1 }));

  // Contour
  setDraw(doc, panel.color);
  doc.setLineWidth(1.2);
  for (let i = 0; i < pts.length; i++) {
    const next = pts[(i + 1) % pts.length];
    doc.line(pts[i][0], pts[i][1], next[0], next[1]);
  }

  // Points + labels
  pts.forEach((p, idx) => {
    setFill(doc, panel.color);
    doc.circle(p[0], p[1], 2, "F");

    const angle = angle0 + (idx / n) * 2 * Math.PI;
    const lx = cx + Math.cos(angle) * (R + 8);
    const ly = cy + Math.sin(angle) * (R + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    setTextColorRGB(doc, 30, 16, 64);
    const lb2 =
      rows[idx].member.length > 12
        ? rows[idx].member.slice(0, 11) + "…"
        : rows[idx].member;
    doc.text(lb2, lx, ly, { align: "center" });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Dessin "aucune donnée" ────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawNoData(doc: jsPDF, panel: PanelDataPDF) {
  const [lr, lg, lb] = lightenRgb(panel.color, 0.94);
  doc.setFillColor(lr, lg, lb);
  roundedRect(doc, ML, 34, CW, 152, 6, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  setTextColor(doc, panel.color);
  doc.text("Aucune donnée disponible", PW / 2, 120, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setTextColorRGB(doc, 120, 100, 160);
  doc.text("Lancez une actualisation pour charger les données.", PW / 2, 132, {
    align: "center",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Footer de page ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function drawFooter(
  doc: jsPDF,
  panel: PanelDataPDF,
  pageNum: number,
  totalPages: number,
) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  setTextColorRGB(doc, 160, 140, 200);
  doc.text(
    `AvDash  ·  ${panel.title}  ·  ${new Date().toLocaleDateString("fr-FR")}`,
    ML,
    PH - 5,
  );
  doc.text(`${pageNum} / ${totalPages}`, PW - MR, PH - 5, { align: "right" });

  // Ligne footer
  const [lr, lg, lb] = lightenRgb(panel.color, 0.6);
  doc.setDrawColor(lr, lg, lb);
  doc.setLineWidth(0.3);
  doc.line(ML, PH - 8, PW - MR, PH - 8);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Hook principal ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

export function useExportPDF() {
  const exportPDF = useCallback(async (options: ExportPDFOptions) => {
    const { filename, dateFrom, dateTo, topN } = options;
    // Filtrer les membres indésirables dans chaque panel
    const panels = options.panels.map(p => ({
      ...p,
      rows: (p.rows ?? []).filter(r => isValidDimension(r.member)),
    }));

    // ── Calcul du nombre total de pages ──────────────────────────────────────
    // Pour le tableau : on calcule combien de pages il faudra selon topN et rowH
    const HEADER_H = 25;
    const FOOTER_H = 10;
    const rowH = 9;
    const PAGE_DATA_BOTTOM = PH - FOOTER_H;
    // Lignes disponibles par page (page 1 = après header panel, pages suivantes idem)
    const rowsPerPage = Math.floor(
      (PAGE_DATA_BOTTOM - HEADER_H - 2 - (rowH + 1)) / rowH,
    );

    let totalPages = 1; // couverture
    for (const panel of panels) {
      for (const chartType of CHART_TYPES) {
        if (chartType === "table" && panel.rows && panel.rows.length > 0) {
          // Nombre de pages nécessaires pour ce tableau
          const tablePages = Math.ceil(panel.rows.length / rowsPerPage);
          totalPages += Math.max(1, tablePages);
        } else {
          totalPages += 1;
        }
      }
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // ── Page couverture ──
    drawCoverPage(doc, panels, topN, dateFrom, dateTo);

    let pageNum = 2;

    // ── Pages par panel ──
    for (const panel of panels) {
      for (const chartType of CHART_TYPES) {
        doc.addPage();

        drawPageHeader(doc, panel, chartType, topN, pageNum, totalPages);

        if (!panel.rows || panel.rows.length === 0) {
          drawNoData(doc, panel);
          drawFooter(doc, panel, pageNum, totalPages);
          pageNum++;
        } else if (chartType === "table") {
          // ── drawTable gère sa propre pagination et retourne le pageNum final ──
          pageNum = drawTable(doc, panel, topN, pageNum, totalPages, chartType);
          drawFooter(doc, panel, pageNum, totalPages);
          pageNum++;
        } else {
          switch (chartType) {
            case "bar":
              drawBarChart(doc, panel, topN);
              break;
            case "line":
              drawLineChart(doc, panel, topN);
              break;
            case "area":
              drawAreaChart(doc, panel, topN);
              break;
            case "pie":
              drawPieChart(doc, panel, topN);
              break;
            case "radar":
              drawRadarChart(doc, panel, topN);
              break;
          }
          drawFooter(doc, panel, pageNum, totalPages);
          pageNum++;
        }
      }
    }

    // ── Téléchargement ──
    doc.save(`${filename}.pdf`);
  }, []);

  return { exportPDF };
}